import type { BestOf, DeuceRule, GameFormat } from '@/types/game-format';

interface ScheduleGameFormat {
  best_of: number | null;
  points_per_game: number | null;
  deciding_game_points?: number | null;
  deuce_rule: string | null;
}

interface ResolveInput {
  schedule: ScheduleGameFormat | null;
  eventGameFormat: Partial<GameFormat> | undefined;
}

interface ResolvedGameFormat {
  bestOf: BestOf;
  pointsPerGame: number;
  decidingGamePoints: number | null;
  deuceRule: DeuceRule;
}

/**
 * Resolves the effective game format for a match.
 *
 * Source-of-truth chain (per field):
 *   bestOf / pointsPerGame : schedule → event → safe default
 *   decidingGamePoints     : schedule → event → no alternate target
 *   deuceRule              : event → schedule → safe default
 *
 * deuceRule prefers the event because draw generation does not currently
 * propagate `event.settings.game_format.deuceRule` into
 * `event_match_schedule.deuce_rule` — the schedule column defaults to
 * 'win_by_2' regardless, so trusting it would silently lose sudden-death
 * configuration. Once draw generation is fixed, this asymmetry can go away.
 */
export function resolveGameFormat({ schedule, eventGameFormat }: ResolveInput): ResolvedGameFormat {
  const bestOf = (schedule?.best_of ?? eventGameFormat?.bestOf ?? 5) as BestOf;
  const pointsPerGame = schedule?.points_per_game ?? eventGameFormat?.pointsPerGame ?? 11;
  const deuceRule = (eventGameFormat?.deuceRule ?? schedule?.deuce_rule ?? 'win_by_2') as DeuceRule;

  const decidingGamePoints =
    schedule?.deciding_game_points ?? eventGameFormat?.decidingGamePoints ?? null;

  return { bestOf, pointsPerGame, decidingGamePoints, deuceRule };
}
